import React, { useState, useEffect, useRef } from 'react';
import { Button, InputGroup, FormControl } from 'react-bootstrap';
import { FaPlus, FaMinus } from 'react-icons/fa';

const QuantitySelector = ({
    value,
    max,
    onChange,
    size = 'sm',
    debounceMs = 0,
    disabled = false,
    allowDecrease = false,
    density = 'normal',
}) => {
    const [localValue, setLocalValue] = useState(value);
    const timerRef = useRef(null);
    const onChangeRef = useRef(onChange);

    // Keep ref updated with latest callback to avoid stale closures in timeout
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        // Only update localValue from props if not currently editing/debouncing
        if (!timerRef.current) {
            setLocalValue(value);
        }
    }, [value]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const triggerChange = (newValue) => {
        // If strictly disabled and NOT allowing decrease, return.
        if (disabled && !allowDecrease) return;

        // If disabled but allowing decrease, only allow if new value < current value
        if (disabled && allowDecrease && newValue >= localValue) return;

        setLocalValue(newValue);

        if (debounceMs > 0) {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                timerRef.current = null;
                if (onChangeRef.current) {
                    onChangeRef.current(newValue);
                }
            }, debounceMs);
        } else {
            if (onChangeRef.current) {
                onChangeRef.current(newValue);
            }
        }
    };

    const handleDecrement = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Allow decrement if not disabled OR if allowDecrease is true
        if (disabled && !allowDecrease) return;

        const currentValue = parseInt(localValue) || 1;
        if (currentValue > 1) {
            triggerChange(currentValue - 1);
        }
    };

    const handleIncrement = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return; // Never allow increment if disabled
        
        const currentValue = parseInt(localValue) || 1;
        if (max === undefined || currentValue < max) {
            triggerChange(currentValue + 1);
        }
    };

    const handleChange = (e) => {
        // Manual input
        if (disabled && !allowDecrease) return;

        let newValue = parseInt(e.target.value);
        
        // Allow clearing the input temporarily
        if (e.target.value === '') {
            setLocalValue('');
            return;
        }

        if (isNaN(newValue)) return;

        // If disabled but allowing decrease, ensure we don't go up
        if (disabled && allowDecrease && newValue > localValue) return;

        if (newValue < 1) newValue = 1;
        if (max !== undefined && newValue > max) newValue = max;
        
        triggerChange(newValue);
    };

    // Determine if the whole component should look disabled
    const isVisuallyDisabled = disabled && !allowDecrease;

    return (
        <InputGroup
            size={size}
            className={`quantity-selector-container quantity-selector-container--${density} ${isVisuallyDisabled ? 'disabled-selector' : ''}`}
            onClick={(e) => e.stopPropagation()}
        >
            <style>
                {`
            .quantity-selector-container {
                width: 110px !important;
                }
            .quantity-selector-container--compact {
                width: 96px !important;
                }
            .disabled-selector {
                opacity: 0.6;
                cursor: not-allowed;
            }
            .disabled-selector * {
                pointer-events: none;
            }
            .quantity-selector-container--compact .btn {
                padding: 0.2rem 0.35rem;
            }
            .quantity-selector-container--compact .form-control {
                padding: 0.2rem 0.2rem;
                font-size: 0.76rem;
            }
            @media (max-width: 767.98px) {
                .quantity-selector-container {
                    width: 90px !important;
                }
                .quantity-selector-container--compact {
                    width: 84px !important;
                }
                .quantity-selector-container .btn {
                    padding: 0.25rem 0.5rem;
                }
                .quantity-selector-container--compact .btn {
                    padding: 0.18rem 0.3rem;
                }
                .quantity-selector-container .form-control {
                    padding: 0.25rem 0.25rem;
                    font-size: 0.8rem;
                }
                .quantity-selector-container--compact .form-control {
                    padding: 0.18rem 0.18rem;
                    font-size: 0.74rem;
                }
            }
                input[type=number]::-webkit-inner-spin-button,
                input[type=number]::-webkit-outer-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                input[type=number] {
                    -moz-appearance: textfield;
                }
                `}
            </style>
            <Button 
                variant="outline-secondary" 
                onClick={handleDecrement}
                disabled={(disabled && !allowDecrease) || parseInt(localValue) <= 1}
                className="d-flex align-items-center justify-content-center"
                style={{ cursor: (disabled && !allowDecrease) ? 'not-allowed' : 'pointer' }}
            >
                <FaMinus size={10} />
            </Button>
            <FormControl
                className="text-center"
                value={localValue}
                onChange={handleChange}
                onClick={(e) => e.stopPropagation()}
                type="number"
                min="1"
                max={max}
                disabled={disabled && !allowDecrease}
                readOnly={disabled && allowDecrease} 
            />
            <Button 
                variant="outline-secondary" 
                onClick={handleIncrement}
                disabled={disabled || (max !== undefined && parseInt(localValue) >= max)}
                className="d-flex align-items-center justify-content-center"
            >
                <FaPlus size={10} />
            </Button>
        </InputGroup>
    );
};

export default QuantitySelector;
